import { InfiniteData, useInfiniteQuery, useMutation, useQuery } from '@tanstack/react-query';
import { FormikErrors, FormikValues } from 'formik';
import { useRef } from 'react';
import { api } from '@/modules/common/api/index.ts';
import { withErrorHandling } from '@/modules/common/utils/error-utils.tsx';
import { invalidateQueriesTransactions } from '@/modules/issuer-dashboard/queries/activities.tsx';
import {
  CreateTransferRequest,
  GetIssuerTransferListResponse,
  GetIssuerTransferListV1SrTransfersIssuerGetParams,
  GetTransferResponse,
  GetTransferV1SrTransfersIdGetParams,
  TransferSetCompletedStatusRequest,
  TransferSetRejectedStatusRequest,
} from 'api';

export const CURRENT_ISSUER_TRANSFERS_QUERY_KEY = 'vestra/issuer-current-transfers';
export const CURRENT_ISSUER_TRANSFER_BY_ID_QUERY_KEY = 'vestra/issuer-current-transfer-by-id';

export const useIssuerTransferListQuery = (
  payload: GetIssuerTransferListV1SrTransfersIssuerGetParams,
  disabled = false,
  register_id?: number,
) => {
  const { page, page_size, ...data } = payload;

  const previousDataRef = useRef<InfiniteData<GetIssuerTransferListResponse> | null>(null);

  return useInfiniteQuery<GetIssuerTransferListResponse>({
    queryKey: [
      CURRENT_ISSUER_TRANSFERS_QUERY_KEY,
      page_size,
      register_id,
      ...Object.entries(data).map(obj => obj[1]),
    ],
    staleTime: 1000 * 60 * 60 * 3, // 3 hours
    queryFn: async ({ pageParam = 0 }) => {
      const response = await api.v1.getIssuerTransferListV1SrTransfersIssuerGet({
        ...payload,
        page: pageParam || page,
      });
      return response.data;
    },
    keepPreviousData: true,
    retry: 2,
    getNextPageParam: ({ total, page, page_size }) => {
      const totalPage = Math.ceil(total / page_size);
      const hasMore = totalPage > page;
      if (hasMore) {
        return page + 1;
      }
    },
    enabled: !disabled,
    refetchInterval: 1000 * 60, //1min
    onSuccess: async data => {
      const previousData = previousDataRef.current;

      if (previousData) {
        const hasStatusChanged = data?.pages.some((newPage, pageIndex) =>
          newPage.data.some((newItem, itemIndex) => {
            const prevItem = previousData?.pages[pageIndex]?.data?.[itemIndex];
            return prevItem && newItem.status !== prevItem.status;
          }),
        );
        if (hasStatusChanged) {
          await invalidateQueriesTransactions({
            updateDividend: false,
          });
        }
      }

      previousDataRef.current = data;
    },
  });
};

export const useIssuerTransferByIdQuery = (
  payload: GetTransferV1SrTransfersIdGetParams,
  disabled = false,
  register_id?: number,
) => {
  return useQuery<GetTransferResponse>({
    queryKey: [CURRENT_ISSUER_TRANSFER_BY_ID_QUERY_KEY, JSON.stringify(payload), register_id],
    staleTime: 1000 * 60 * 60 * 3, // 3 hours
    queryFn: async () => {
      const response = await api.v1.getTransferV1SrTransfersIdGet(payload);
      return response.data;
    },
    keepPreviousData: true,
    retry: 2,
    enabled: !disabled,
  });
};

export const useTransferArchiveByIdMutation = () => {
  return useMutation({
    mutationFn: async (id: number) => {
      const response = await withErrorHandling(
        api.v1.archiveTransferV1SrArchivingTransferIdPost({ id }),
        {
          loading: 'Transfer archiving...',
          success: 'Transfer archived!',
        },
      );

      return response.data;
    },
    onSuccess: async () => {
      await invalidateQueriesTransactions({
        updateDividend: false,
      });
    },
  });
};

export const useIssuerCreateTransferMutation = ({
  setErrors,
}: {
  setErrors: (errors: FormikErrors<FormikValues>) => void;
}) => {
  return useMutation({
    mutationFn: async (payload: CreateTransferRequest & { register_id?: number }) => {
      const { register_id, ...data } = payload;
      const response = await withErrorHandling(
        api.v1.createIssuerTransferV1SrTransfersIssuerPost(
          data,
          register_id
            ? {
                headers: {
                  ['xRegister-ID']: register_id,
                },
              }
            : undefined,
        ),
        {
          loading: 'Creating transfer...',
          success: 'Transfer created',
          setErrors,
        },
      );

      return response.data;
    },
    onSuccess: async () => {
      await invalidateQueriesTransactions({ updateDividend: false });
    },
  });
};

export const useTransferSetCompletedMutation = ({ onSuccess }: { onSuccess?: () => void }) => {
  return useMutation({
    mutationFn: async (payload: TransferSetCompletedStatusRequest) => {
      const response = await withErrorHandling(
        api.v1.transferSetCompletedV1SrTransfersStatusCompletedPatch(payload),
        {
          loading: 'Updating transfer...',
          success: 'Transfer updated',
        },
      );

      return response.data;
    },
    onSuccess: async () => {
      await invalidateQueriesTransactions({ updateDividend: false });

      onSuccess && onSuccess();
    },
  });
};

export const useTransferSetRejectedMutation = () => {
  return useMutation({
    mutationFn: async (payload: TransferSetRejectedStatusRequest) => {
      const response = await withErrorHandling(
        api.v1.transferSetRejectV1SrTransfersStatusRejectedPatch(payload),
        {
          loading: 'Updating transfer...',
          success: 'Transfer updated',
        },
      );

      return response.data;
    },
    onSuccess: async () => {
      await invalidateQueriesTransactions({ updateDividend: false });
    },
  });
};
